// 配役結果をシェアするボタン。アイコンを押すだけで各サービスの投稿画面が開く。

import { siBluesky, siFacebook, siInstagram, siLine, siThreads, siTiktok, siX } from "simple-icons";
import type { MemberView } from "../shared/api";
import type { Role } from "../shared/content";
import { shareText, shareUrl } from "../shared/share";
import { renderCardImage, showCardPreview } from "./card-image";
import { content, t } from "./lang";

interface Service {
  id: string;
  label: string;
  icon: { path: string; hex: string };
  /** リンクで投稿画面を開くサービス。無いものはキャラカード画像で共有する */
  url?: (text: string, url: string, hashtags: string[]) => string;
  /** パソコンで投稿するときに開くページ (画像はそこで選んでもらう) */
  webUpload?: string;
}

const enc = encodeURIComponent;
const hashtagText = (tags: string[]) => tags.map((tag) => `#${tag}`).join(" ");

const SERVICES: Service[] = [
  {
    id: "x",
    label: "X",
    icon: siX,
    url: (text, url, tags) =>
      `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}&hashtags=${enc(tags.join(","))}`,
  },
  // Instagram と TikTok は、Web から投稿画面を開く仕組みがないので、画像を共有メニューで渡す
  { id: "instagram", label: "Instagram", icon: siInstagram, webUpload: "https://www.instagram.com/" },
  {
    id: "line",
    label: "LINE",
    icon: siLine,
    url: (text, url) => `https://social-plugins.line.me/lineit/share?url=${enc(url)}&text=${enc(text)}`,
  },
  {
    id: "facebook",
    label: "Facebook",
    icon: siFacebook,
    // Facebook は文を前もって入れられない。リンク先の画像と説明がカードで表示される
    url: (_text, url) => `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
  },
  {
    id: "threads",
    label: "Threads",
    icon: siThreads,
    url: (text, url, tags) => `https://www.threads.net/intent/post?text=${enc(`${text} ${hashtagText(tags)}\n${url}`)}`,
  },
  {
    id: "bluesky",
    label: "Bluesky",
    icon: siBluesky,
    url: (text, url, tags) => `https://bsky.app/intent/compose?text=${enc(`${text} ${hashtagText(tags)}\n${url}`)}`,
  },
  { id: "tiktok", label: "TikTok", icon: siTiktok, webUpload: "https://www.tiktok.com/upload" },
];

const iconSvg = (path: string) =>
  `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${path}"/></svg>`;

export function shareButtonsHtml(role: Role): string {
  const ui = t();
  const button = (id: string, label: string, path: string, color: string) => `
    <li>
      <button type="button" class="share-btn" data-share="${id}" aria-label="${ui.shareWith(label)}">
        <span class="share-icon" style="--brand:${color}">${iconSvg(path)}</span>
        <span class="share-label">${label}</span>
      </button>
    </li>`;
  const url = shareUrl(content(), role);
  return `
    <section class="role-section share" aria-labelledby="share-title">
      <h2 id="share-title">${ui.shareTitle}</h2>
      <ul class="share-list">
        ${SERVICES.map((s) => button(s.id, s.label, s.icon.path, `#${s.icon.hex}`)).join("")}
      </ul>
      <div class="share-link">
        <label class="visually-hidden" for="share-url">${ui.shareLinkLabel}</label>
        <input id="share-url" class="share-url" value="${url}" readonly />
        <button type="button" class="share-copy" data-share="copy">${ui.copy}</button>
      </div>
      ${
        "share" in navigator
          ? `<button type="button" class="share-more" data-share="more">${ui.shareMore}</button>`
          : ""
      }
      <p class="share-status" role="status"></p>
    </section>`;
}

export async function handleShare(id: string, role: Role, me: MemberView, statusEl: Element | null): Promise<void> {
  const c = content();
  const ui = t();
  const text = shareText(c, role);
  const url = shareUrl(c, role);
  const hashtags = hashtagText(c.share.hashtags);
  const status = (message: string) => {
    if (statusEl) statusEl.textContent = message;
  };

  const service = SERVICES.find((s) => s.id === id);
  if (service?.url) {
    window.open(service.url(text, url, c.share.hashtags), "_blank", "noopener,noreferrer,width=640,height=720");
    return;
  }

  if (id === "copy") {
    const copyButton = document.querySelector<HTMLButtonElement>(".share-copy");
    try {
      await navigator.clipboard.writeText(`${text} ${hashtags}\n${url}`);
      status(ui.copyDone);
      if (copyButton) {
        copyButton.textContent = ui.copied;
        setTimeout(() => (copyButton.textContent = ui.copy), 2000);
      }
    } catch {
      // コピーできない環境では、リンクの欄を選んでおく
      document.querySelector<HTMLInputElement>(".share-url")?.select();
      status(ui.copyFailed);
    }
    return;
  }

  if (id === "more") {
    try {
      await navigator.share({ title: "Bard Club", text: `${text} ${hashtags}`, url });
    } catch {
      // キャンセルされたときは何もしない
    }
    return;
  }

  // Instagram / TikTok: スマホはキャラカード画像を共有メニューでアプリに渡す。
  // パソコンの共有メニューには Instagram / TikTok が無いので、画像を保存して、それぞれのサイトで投稿してもらう
  if (service) {
    const postText = `${text} ${hashtags} ${url}`;
    const isTouch = matchMedia("(pointer: coarse)").matches;
    // 押した直後でないとコピーできないブラウザがあるので、画像を作る前にコピーしておく
    const copied = isTouch ? false : await navigator.clipboard?.writeText(postText).then(() => true, () => false);
    status(ui.makingCard);
    const blob = await renderCardImage(role, me);
    const file = new File([blob], `bard-club-${role.id}.png`, { type: "image/png" });
    status("");
    if (!isTouch && service.webUpload) {
      showCardPreview(blob, file.name, postText, ui.postFromPc(service.label, url, !!copied), {
        label: ui.openService(service.label),
        url: service.webUpload,
      });
      return;
    }
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], text: `${text} ${hashtags} ${url}` });
        return;
      } catch (e) {
        if ((e as DOMException).name === "AbortError") return;
      }
    }
    showCardPreview(
      blob,
      file.name,
      `${text} ${hashtags} ${url}`,
      ui.postFromApp(service.label, url),
    );
  }
}
